import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { queryKeys } from '@/shared/lib/query/queryKeys';
import { ApiError, getErrorMessage } from '@/shared/api/errors';
import * as api from './clientsApi';

export function useClientsQuery(
  filters: Record<string, unknown> = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.clients.list(filters),
    queryFn: () => api.fetchClients(filters),
    refetchOnWindowFocus: false,
    enabled: options?.enabled ?? true,
  });
}

export function useCreateClientMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createClient,
    onSuccess: (data) => {
      const payload = data as { message?: string };
      toast.success(
        payload?.message ||
          'Client created. Login details sent to the client.',
      );
      void qc.invalidateQueries({ queryKey: queryKeys.clients.root });
    },
    onError: (error) => {
      if (
        error instanceof ApiError &&
        (error.code === 'COMPANY_REGISTERED_WITH_OTHER_AGENCY' ||
          isEmailAlreadyExistsError(error))
      ) {
        return;
      }
      toast.error(getErrorMessage(error, 'Failed to create client'));
    },
  });
}

export function isEmailAlreadyExistsError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  const msg = error.message.toLowerCase();
  const code = String(error.code || '').toUpperCase();
  return (
    (code.includes('EMAIL') &&
      (code.includes('EXIST') || code.includes('DUPLICATE'))) ||
    msg.includes('email already exists') ||
    msg.includes('user with email already exists') ||
    msg.includes('email already registered') ||
    msg.includes('email is already')
  );
}

export function useUpdateClientMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      clientId,
      payload,
    }: {
      clientId: string;
      payload: Record<string, unknown>;
    }) => api.updateClient(clientId, payload),
    onSuccess: (data) => {
      toast.success((data as { message?: string })?.message || 'Client updated');
      void qc.invalidateQueries({ queryKey: queryKeys.clients.root });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to update client'));
    },
  });
}

export function useDeleteClientMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.deleteClient,
    onSuccess: (data) => {
      toast.success((data as { message?: string })?.message || 'Client deleted');
      void qc.invalidateQueries({ queryKey: queryKeys.clients.root });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to delete client'));
    },
  });
}
